import styled from '@emotion/styled';
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';

type DashboardWidgetContainerProps = {
  children: React.ReactNode;
  header: string;
  subheader?: string;
  navigateTo?: () => void;
  fullWidth?: boolean;
  headerContent?: React.ReactNode;
  headerAction?: {
    icon: SvgNames;
    onClick: () => void;
    ariaLabel?: string;
    /**
     * Some icon assets don't fill their viewBox the same way as others (e.g.
     * ArrowsInSimple renders visibly smaller than ArrowsOutSimple), so callers
     * can bump this up to visually match neighboring icons. Defaults to 24.
     */
    iconSize?: number;
  };
  /** When true, render the header as provided instead of forcing uppercase. */
  preserveHeaderCase?: boolean;
};

const DashboardWidgetContainer = ({
  children,
  header,
  subheader,
  navigateTo,
  fullWidth = false,
  headerContent,
  headerAction,
  preserveHeaderCase = false,
}: DashboardWidgetContainerProps) => {
  return (
    <Container $fullWidth={fullWidth}>
      <HeaderContainer $hasHeader={!!header}>
        <HeaderTitleAndSubTitleContainer>
          <Typography>
            {preserveHeaderCase ? header : header.toUpperCase()}
          </Typography>
          {!!subheader && (
            <Typography size="small" color={tokens.TINTS_OFF_WHITE_20}>
              {subheader}
            </Typography>
          )}
        </HeaderTitleAndSubTitleContainer>
        <HeaderIconsContainer>
          {headerContent}
          {!!headerAction && (
            <HeaderActionButton
              role="button"
              aria-label={headerAction.ariaLabel}
              onClick={headerAction.onClick}>
              <StyledSvgIcon
                name={headerAction.icon}
                width={headerAction.iconSize ?? 24}
                height={headerAction.iconSize ?? 24}
                color={tokens.BRAND_HEROBLUE}
              />
            </HeaderActionButton>
          )}
          {!!navigateTo && (
            <div
              onClick={() => {
                navigateTo();
              }}>
              <StyledSvgIcon
                name={SvgNames.ChevronRightCircle}
                width={24}
                height={24}
                color={tokens.BRAND_HEROBLUE}
              />
            </div>
          )}
        </HeaderIconsContainer>
      </HeaderContainer>
      <ContentContainer>{children}</ContentContainer>
    </Container>
  );
};

const StyledSvgIcon = styled(SvgIcon)`
  cursor: pointer;
`;

const Container = styled.div<{ $fullWidth: boolean }>`
  border-radius: 8px;
  min-width: 300px;
  height: calc(100% - 2px);
  ${({ $fullWidth }) => $fullWidth && 'width: 100%'};
  display: flex;
  flex-direction: column;
  background-color: rgba(31, 38, 71, 0.04);

  * {
    box-sizing: border-box;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
`;

const HeaderTitleAndSubTitleContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const HeaderIconsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const HeaderActionButton = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const HeaderContainer = styled.div<{ $hasHeader: boolean }>`
  display: flex;
  height: ${({ $hasHeader }) => ($hasHeader ? '52px' : '14px')};
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
`;

export { DashboardWidgetContainer };
